import api from "../../api/api";
import { V2_BASE_URL } from "../../api/bases";
import { getPracticeId } from "../../config/practice";

/* Privileges screen revamp (owner 11-03). The NEW v2 routes (gateway api/privileges/admin.py), pinned to v2 like
   the department calls: they exist on v2 only and answer user administrators only (403 otherwise).
   Writes are add / remove lists applied by the server to the stored list, so ids the screen does not show
   (retired, turned off, unknown) are never lost - unlike the old PUT /user, which replaced the whole list. */
const V2 = { baseURL: V2_BASE_URL, _gw: "v2-pinned" };
const P = () => getPracticeId();

export const fetchCatalog = () => api.get(`/v1/${P()}/privileges/catalog`, V2);
export const fetchUserPrivileges = (rowid) => api.get(`/v1/${P()}/users/${rowid}/privileges`, V2);
export const saveUserPrivileges = (rowid, { add = [], remove = [], reason = null, dryRun = false } = {}) =>
  api.put(`/v1/${P()}/users/${rowid}/privileges`, { add, remove, reason, dry_run: !!dryRun }, V2);
export const fetchMatrix = (params) => api.get(`/v1/${P()}/users/privilege-matrix`, { ...V2, params });
export const bulkPrivileges = ({ userIds, add = [], remove = [], reason = null, dryRun = true }) =>
  api.post(`/v1/${P()}/users/privileges/bulk`, { user_ids: userIds, add, remove, reason, dry_run: dryRun !== false }, V2);
export const fetchRoleTemplate = (roleid) => api.get(`/v1/${P()}/roles/${roleid}/template`, V2);
export const saveRoleTemplate = (roleid, privilegeIds, dryRun = false) =>
  api.put(`/v1/${P()}/roles/${roleid}/template`, { privilege_ids: privilegeIds, dry_run: !!dryRun }, V2);
export const applyRoleTemplate = (roleid, { userIds = null, mode = "add", reason = null, dryRun = true } = {}) =>
  api.post(`/v1/${P()}/roles/${roleid}/template/apply`,
    { user_ids: userIds && userIds.length ? userIds : null, mode, reason, dry_run: dryRun !== false }, V2);

/* user_master.permissions as care receives it: a JSON string list, a list, "null", "" or garbage. Never throws
   (stephaniec's 'null' crashed the old Edit User). */
export const parsePermissions = (raw) => {
  let value = raw;
  if (typeof raw === "string") {
    const t = raw.trim();
    if (!t || t === "null") return [];
    try {
      value = JSON.parse(t);
    } catch (e) {
      return [];
    }
  }
  if (!Array.isArray(value)) return [];
  const out = [];
  value.forEach((v) => {
    if (v === null || v === undefined || typeof v === "object") return;
    const s = String(v).trim();
    if (s && !out.includes(s)) out.push(s);
  });
  return out;
};

const str = (v) => String(v);

/* The catalog's editable rows grouped by area (catalog.areas order), plus the read-only Retired list. */
export const groupCatalog = (catalog) => {
  const areas = (catalog?.areas || []).filter((a) => a.key !== "retired");
  const items = Array.isArray(catalog?.privileges) ? catalog.privileges : [];
  const groups = areas.map((a) => ({ ...a, items: items.filter((p) => p.editable && p.area === a.key) }))
    .filter((g) => g.items.length > 0);
  // a hidden row written together with a shown one (49 with 51 Attendance) is that switch, not a retired row
  const merged = (p) => (p.written_with || []).some((w) => items.some((x) => String(x.id) === String(w) && x.editable));
  const retired = items.filter((p) => !p.editable && !merged(p));
  return { groups, retired };
};

export const editableItems = (catalog) => (catalog?.privileges || []).filter((p) => p.editable);

/* Ticks the screen starts from: an editable row is ticked when the user holds it OR a row written with it
   (49 / 51 Attendance). */
export const initialSelection = (catalog, permissions) => {
  const held = new Set(parsePermissions(permissions));
  const sel = new Set();
  editableItems(catalog).forEach((p) => {
    if (held.has(str(p.id)) || (p.written_with || []).some((w) => held.has(str(w)))) sel.add(str(p.id));
  });
  return sel;
};

/* {add, remove} (string ids) between the starting ticks and the current ones; editable rows only. */
export const diffSelection = (initial, current) => {
  const add = [...current].filter((id) => !initial.has(id));
  const remove = [...initial].filter((id) => !current.has(id));
  const num = (a, b) => Number(a) - Number(b);
  return { add: add.sort(num), remove: remove.sort(num) };
};

export const matchesSearch = (item, q) => {
  const needle = String(q || "").trim().toLowerCase();
  if (!needle) return true;
  return [item.label, item.description, item.privilege, item.page, String(item.id)]
    .some((v) => String(v || "").toLowerCase().includes(needle));
};

/* What "Apply role template" would do to the current ticks. mode "add" = tick the template's privileges;
   "replace" = also untick visible privileges outside the template (User administration is never unticked). */
export const templatePreview = (catalog, roleId, current, mode = "add") => {
  const role = (catalog?.roles || []).find((r) => str(r.id) === str(roleId));
  const editable = new Set(editableItems(catalog).map((p) => str(p.id)));
  const template = new Set(((role && role.template_visible) || []).map(str).filter((id) => editable.has(id)));
  const keep = new Set((catalog?.users_privilege_ids || []).map(str));
  const next = new Set(current);
  const add = [];
  const remove = [];
  template.forEach((id) => { if (!next.has(id)) { next.add(id); add.push(id); } });
  if (mode === "replace") {
    [...current].forEach((id) => {
      if (!template.has(id) && !keep.has(id)) { next.delete(id); remove.push(id); }
    });
  }
  return { role, template: [...template], add, remove, next };
};

export const labelOf = (catalog, id) => {
  const p = (catalog?.privileges || []).find((x) => str(x.id) === str(id));
  return p ? p.label : `#${id}`;
};

export const errorText = (err, fallback = "Something went wrong.") => {
  const d = err?.response?.data;
  if (typeof d?.message === "string" && d.message) return d.message;
  if (typeof d?.detail === "string" && d.detail) return d.detail;
  if (Array.isArray(d?.detail) && d.detail[0]?.msg) return d.detail[0].msg;
  if (typeof err?.message === "string" && err.message && !err.response) return fallback;
  return fallback;
};

/* Profile validation that never blocks a privileges / department save: a field is checked only when the admin
   changed it (6 logins have stored phones that are not 10 digits). */
export const EMAIL_RE = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
export const PHONE_RE = /^[0-9]{10}$/;
export const profileErrors = (initial, current) => {
  const errors = {};
  if (String(current.email || "") !== String(initial.email || "") && !EMAIL_RE.test(current.email || "")) {
    errors.email = "Please enter a valid email.";
  }
  if (String(current.phone || "") !== String(initial.phone || "") && !PHONE_RE.test(current.phone || "")) {
    errors.phone = "Please enter a 10-digit phone number.";
  }
  return errors;
};
export const PROFILE_FIELDS = ["firstname", "lastname", "username", "email", "phone", "status", "grole"];
export const profileChanged = (initial, current) =>
  PROFILE_FIELDS.some((k) => String(current[k] ?? "") !== String(initial[k] ?? ""));

/* The groups the UserSheet's Privileges tab shows (rail + cards).
   With the v2 catalog: one group per area (plain labels, descriptions), then a read-only "Retired" group.
   Without it (v2 did not answer): the v1 privileges/get rows by page, as Teja's sheet always showed them.
   Every item: {id (string), label, description, editable, reason, page}. */
export const sheetGroups = ({ catalog, privileges, pages, privLabel = (n) => n }) => {
  if (catalog && Array.isArray(catalog.privileges)) {
    const { groups, retired } = groupCatalog(catalog);
    const out = groups.map((g) => ({
      key: g.key, label: g.label, editable: true,
      items: g.items.map((p) => ({ id: str(p.id), label: p.label, description: p.description, editable: true, page: p.page })),
    }));
    if (retired.length) {
      out.push({
        key: "retired", label: "Retired", editable: false,
        items: retired.map((p) => ({
          id: str(p.id), label: p.label, editable: false, page: p.page,
          description: [p.reason || p.description, p.legacy_note].filter(Boolean).join(" "),
        })),
      });
    }
    return out;
  }
  return (pages || []).map((pg) => ({
    key: pg.key, label: pg.label, editable: true,
    items: (privileges || []).filter((p) => p.page === pg.key)
      .map((p) => ({ id: str(p.id), label: privLabel(p.privilege), description: "", editable: true, page: p.page })),
  })).filter((g) => g.items.length > 0);
};

/* The role's template among the shown, editable items: catalog roles[].template_visible, else the v1 rows'
   role lists (null-safe; the old code threw on a bad JSON role). */
export const roleTemplateIds = ({ catalog, privileges, role }) => {
  if (!role) return null;
  if (catalog && Array.isArray(catalog.roles)) {
    const r = catalog.roles.find((x) => str(x.id) === str(role.id));
    return r ? (r.template_visible || []).map(str) : [];
  }
  return (privileges || []).filter((p) => {
    try {
      const list = JSON.parse(p.role || "[]");
      return Array.isArray(list) && list.map(str).includes(str(role.id));
    } catch (e) {
      return false;
    }
  }).map((p) => str(p.id));
};

/* What "Apply role template" does to the ticks (array of string ids). add: tick the template; replace: also
   untick shown editable privileges outside it (User administration is never unticked). */
export const applyTemplate = ({ template, current, editableIds, keepIds = [], mode = "add" }) => {
  const cur = (current || []).map(str);
  const tpl = (template || []).map(str).filter((id) => editableIds.includes(id));
  const add = tpl.filter((id) => !cur.includes(id));
  const keep = new Set(keepIds.map(str));
  const remove = mode === "replace"
    ? cur.filter((id) => editableIds.includes(id) && !tpl.includes(id) && !keep.has(id)) : [];
  const next = [...cur.filter((id) => !remove.includes(id)), ...add];
  return { add, remove, next };
};

/* ids plus the rows written together with them (catalog written_with: 49 + 51 Attendance). */
export const expandWritten = (catalog, ids) => {
  const out = (ids || []).map(str);
  (catalog?.privileges || []).forEach((p) => {
    if (out.includes(str(p.id))) (p.written_with || []).forEach((w) => { if (!out.includes(str(w))) out.push(str(w)); });
  });
  return out;
};
